/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useTranslation } from 'react-i18next';
import { LaunchUnitCard } from './LaunchUnitCard';
import type { LaunchBoardUnit } from '../types';

export interface LaunchBoardGridProps {
  units: LaunchBoardUnit[];
  selectedUnitId?: string | null;
  onSelectUnit: (unit: LaunchBoardUnit) => void;
}

/**
 * Formats remaining hold seconds as mm:ss.
 *
 * @param seconds - Remaining seconds from the board.
 * @returns Formatted countdown or empty string.
 */
export function formatCountdown(seconds: number | null | undefined): string {
  if (seconds == null || seconds < 0) return '';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/**
 * Grid of launch units with allocation status chips.
 *
 * @param props - Units and selection handler.
 * @returns Board grid element.
 */
export function LaunchBoardGrid({ units, selectedUnitId, onSelectUnit }: LaunchBoardGridProps) {
  const { t } = useTranslation('features/launches');

  return (
    <div
      data-testid="launch-board-grid"
      className="grid grid-cols-2 gap-[var(--space-3)] sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5"
    >
      {units.map((unit) => (
        <LaunchUnitCard
          key={unit.unit_id}
          unit={unit}
          selected={unit.unit_id === selectedUnitId}
          statusLabel={t(`status.${unit.allocation_status}`)}
          countdownLabel={
            unit.allocation_status === 'held'
              ? formatCountdown(unit.remaining_seconds)
              : undefined
          }
          onSelect={onSelectUnit}
        />
      ))}
    </div>
  );
}
